import { TokenSetType } from '../../common/services/auth';
import {
  Customer,
  Entity,
  EntityResponse,
  EntityServiceInterface,
  EntityUser,
  ShopInterface,
} from '../entity';
import { gatewayService } from '../../common/services/gateway';
import { Method } from 'axios';
import { loggerService } from '../../common/services/logger';

class EntityService implements EntityServiceInterface {
  private async sendRequest<T>(
    method: Method,
    uri: string,
    data?: unknown,
    tokenSet?: TokenSetType,
  ): Promise<T> {
    return gatewayService
      .sendRequest<EntityResponse<T>>(method, uri, data, tokenSet)
      .then((response) => response.result);
  }

  async createCustomer(
    data: unknown,
    tokenSet?: TokenSetType,
  ): Promise<Entity> {
    try {
      return this.sendRequest('POST', '/customer', data, tokenSet);
    } catch (error) {
      loggerService.error('Error on createCustomer:', error);
      throw new Error(`Error on createCustomer: ${error}`);
    }
  }

  async createEntity(data: unknown, tokenSet?: TokenSetType): Promise<Entity> {
    try {
      return this.sendRequest('POST', '/entity', data, tokenSet);
    } catch (error) {
      loggerService.error('Error on createEntity:', error);
      throw new Error(`Error on createEntity: ${error}`);
    }
  }

  // TODO: Validate this function
  async createEntityUser(
    data: Record<string, unknown>,
    tokenSet?: TokenSetType,
  ): Promise<EntityUser> {
    try {
      data.firstName = data.firstName || data.name;
      data.email = data.email && `${data.email}`.toLowerCase();

      if (!data.entityId) {
        throw new Error('invalidPartner');
      }

      const shop = data.shop as ShopInterface;

      if (shop) {
        const validatePartnerId = await this.validatePartnerId(
          data.entityId as string,
          shop.partnerId,
        );

        if (!validatePartnerId) {
          throw new Error('partnerId is not valid');
        }
      }

      return this.sendRequest('POST', '/entityuser', data, tokenSet);
    } catch (error) {
      loggerService.error('Error on createEntityUser:', error);
      throw new Error(`Error on createEntityUser: ${error}`);
    }
  }

  async deleteEntity(uid: string, tokenSet?: TokenSetType): Promise<void> {
    try {
      await this.sendRequest('DELETE', `/entity/${uid}`, undefined, tokenSet);
    } catch (error) {
      loggerService.error('Error on deleteEntity:', error);
      throw new Error(`Error on deleteEntity: ${error}`);
    }
  }

  async getAllEntities(tokenSet?: TokenSetType): Promise<Entity[]> {
    try {
      return this.sendRequest('GET', '/entity', undefined, tokenSet);
    } catch (error) {
      loggerService.error('Error on getAllEntities:', error);
      throw new Error(`Error on getAllEntities: ${error}`);
    }
  }

  async getCustomerById(
    id: string,
    tokenSet?: TokenSetType,
  ): Promise<Customer> {
    try {
      return this.sendRequest('GET', `/customer/${id}`, undefined, tokenSet);
    } catch (error) {
      loggerService.error('Error on getCustomerById:', error);
      throw new Error(`Error on getCustomerById: ${error}`);
    }
  }

  async getEntity(entityId: string, tokenSet?: TokenSetType): Promise<Entity> {
    try {
      return this.sendRequest(
        'GET',
        `/entity/${entityId}`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on getEntity:', error);
      throw new Error(`Error on getEntity: ${error}`);
    }
  }

  async getEntityUser(
    query: Record<string, unknown>,
    tokenSet?: TokenSetType,
  ): Promise<EntityUser> {
    try {
      return this.sendRequest(
        'GET',
        `/entityuser?query=${JSON.stringify(query)}`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on getEntityUser:', error);
      throw new Error(`Error on getEntityUser: ${error}`);
    }
  }

  async getEntityUserByEmail(
    email: string,
    tokenSet?: TokenSetType,
  ): Promise<EntityUser> {
    try {
      return this.sendRequest(
        'GET',
        `/entityuser?query=${JSON.stringify({ query: { email } })}`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on getEntityUserByEmail:', error);
      throw new Error(`Error on getEntityUserByEmail: ${error}`);
    }
  }

  async updateEntity(
    uid: string,
    data: unknown,
    tokenSet?: TokenSetType,
  ): Promise<Entity> {
    try {
      return this.sendRequest('PUT', `/entity/${uid}`, data, tokenSet);
    } catch (error) {
      loggerService.error('Error on updateEntity:', error);
      throw new Error(`Error on updateEntity: ${error}`);
    }
  }

  async updateEntityUser(
    id: string,
    data: unknown,
    tokenSet?: TokenSetType,
  ): Promise<EntityUser> {
    try {
      return this.sendRequest('PUT', `/entityuser/${id}`, data, tokenSet);
    } catch (error) {
      loggerService.error('Error on updateEntityUser:', error);
      throw new Error(`Error on updateEntityUser: ${error}`);
    }
  }

  async validatePartnerId(
    entityKey: string,
    partnerId: string,
    tokenSet?: TokenSetType,
  ): Promise<unknown> {
    try {
      return this.sendRequest(
        'GET',
        `/entity/${entityKey}/validate/partnerId/${partnerId}`,
        undefined,
        tokenSet,
      );
    } catch (error) {
      loggerService.error('Error on validatePartnerId:', error);
      throw new Error(`Error on validatePartnerId: ${error}`);
    }
  }
}

export const entityService = new EntityService();
